/**
 * A picture in an article's words (piece 30, Article Editor; decision 0800).
 *
 * 🔒 A picture is a File: the editor's picture tool uploads it through the
 * file pipeline, where it is held Pending until the scan clears it, and the
 * body stores `<img data-kb-file="<id>">` with no address. The page and the PDF
 * resolve the id when they draw it. 🔨 A picture never loads from the internet:
 * the article's sanitiser drops every `src` it is handed.
 */

/** The four the picture tool accepts: the rasters a browser and the PDF renderer both draw. An SVG is a document, not a picture. */
export const ARTICLE_PICTURE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const;

/** Five megabytes: a screenshot or a photograph of a panel, and the PDF carries each one inline. */
export const ARTICLE_PICTURE_MAX_BYTES = 5 * 1024 * 1024;

/** The seeded FileType every article picture takes; the picture tool never asks for one. */
export const ARTICLE_PICTURE_FILE_TYPE_NAME = 'Article Picture';

export function isArticlePictureMime(mimeType: string): boolean {
  return (ARTICLE_PICTURE_MIME_TYPES as readonly string[]).includes(mimeType);
}

/** Which rule an upload failed, the type judged first, or null where it failed neither. */
export function articlePictureProblem(upload: { mimeType: string; sizeBytes: number }): 'type' | 'size' | null {
  if (!isArticlePictureMime(upload.mimeType)) return 'type';
  if (upload.sizeBytes > ARTICLE_PICTURE_MAX_BYTES) return 'size';
  return null;
}

/** The API's own address for one picture of one article, relative to the API root (piece 1). */
export function articlePicturePath(articleId: string, fileId: string): string {
  return `/knowledge/articles/${articleId}/pictures/${fileId}`;
}

/** Where the picture tool uploads, under the article it belongs to. */
export function articlePicturesPath(articleId: string): string {
  return `/knowledge/articles/${articleId}/pictures`;
}

/** The picture's bytes as the PDF carries them: the renderer refuses every request a sheet makes, so the bytes ride in the markup. */
export function articlePictureDataUri(mimeType: string, bytes: { toString(encoding: 'base64'): string }): string | null {
  return isArticlePictureMime(mimeType) ? `data:${mimeType};base64,${bytes.toString('base64')}` : null;
}
